import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import map from 'lodash/map';
import SubstanceDruglikenessComponent from '@Components/Results/substances/substance-druglikeness.component';
import AccordionHeader from '@Components/Results/common/accordion-header.component';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import { resultsConfig } from '@Constants/Results';
import * as Spinners from '@Services/Spinners';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import SubstanceIdentificationData from '@Components/Results/substances/substance-identification-data.component';
import SubstanceSubPropertyComponent from '@Components/Results/substances/substance-sub-property.component';
import SubstanceBioData from '@Components/Results/substances/substance-bio-data.component';
import { contextFromMetadata } from '@Components/Results/utils/metadata';
import { getFactCount } from '@Components/Results/substances/utils/factCountHelper';
import Catalyst from '@Services/Catalyst';
import Slugify from '@Utils/Slugify';

const childView = resultsConfig.INITIAL_ITEMS_AMOUNT.substanceProperty.children;

export default class SubstancePropertyComponent extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      childLimit: childView,
    };

    this.id = `accordion-${Slugify(props.fact.title)}-${props.substanceIndex}`;
  }

  toggleAccordion = () => {
    const {
      metadata,
      fact: { id, open, title },
      toggle,
    } = this.props;

    if (!open) {
      if (metadata.isViewPreview) {
        Catalyst.trackContentLinkClick(title, `query:preview:${contextFromMetadata(metadata)}`);
      } else {
        Catalyst.trackContentLinkClick(title);
      }

      Spinners.activate(this.id);
      toggle(id).finally(() => Spinners.terminate(this.id));
    } else {
      toggle(id);
    }
  };

  showMore = () => this.setState((state) => ({ childLimit: state.childLimit + childView }));

  renderShowMoreButton() {
    return (
      <div className="els-btn-group-center">
        <button type="button" className="els-btn els-btn-sm els-btn-tertiary e2e-load-more" onClick={this.showMore}>
          <svg className="els-btn-icon">
            <use xlinkHref="#icon-plus" />
          </svg>
          <span>Load More</span>
        </button>
      </div>
    );
  }

  render() {
    const { substance, fact, substanceIndex, details, database, metadata, zoom } = this.props;
    const { childLimit } = this.state;
    const { children = [], className, title, type, id, open } = fact;
    const count = getFactCount(fact, substance);

    const itemClasses = classnames(['rx-accordion-item', { 'no-nested-data': type === 'calc' }, { closed: !open }]);

    return (
      <li className={itemClasses}>
        <AccordionHeader className={className} accordionId={this.id} isOpen={open} toggle={this.toggleAccordion}>
          {title} {count ? <span className="countLabel">- {count}</span> : null}
        </AccordionHeader>

        <RxSpinnerComponent spinnerId={this.id} position="relative" />

        {open && (
          <div id={this.id} className="rx-accordion-content u-els-padding-top-1x">
            {type !== 'category' && type !== 'calc' && (
              <ul className="rx-accordion small substance-results-nested-accordion">
                {id === 'ID' && <SubstanceIdentificationData metadata={metadata} details={details} database={database} zoom={zoom} />}
                {map(
                  children.slice(0, childLimit),
                  (child, index) =>
                    child.id !== 'SEQ' && (
                      <SubstanceSubPropertyComponent
                        substance={substance}
                        details={details}
                        key={index}
                        metadata={metadata}
                        child={child}
                        fact={fact}
                        substanceIndex={substanceIndex}
                        database={database}
                        substanceId={details.id}
                        isDefaultOpen={fact.id === 'PRODD'} // HIT_DATA>PROD should not open and render accordion, PRODD>PROD opposite
                      />
                    )
                )}
                {children.length > childLimit && this.renderShowMoreButton()}
              </ul>
            )}

            {type === 'calc' && fact.drugData && <SubstanceDruglikenessComponent drugData={fact.drugData} />}

            {
              /* bio data tables for Heatmap bioactivities popup */
              metadata.heatmapMode && type === 'category' && <SubstanceBioData metadata={metadata} fact={fact} substanceId={details.id} />
            }
          </div>
        )}
      </li>
    );
  }
}

SubstancePropertyComponent.propTypes = {
  metadata: MetadataPropTypesShape,
  substance: PropTypes.object.isRequired,
  fact: PropTypes.object.isRequired,
  details: PropTypes.object.isRequired,
  database: PropTypes.object.isRequired,
  substanceIndex: PropTypes.string.isRequired,
  toggle: PropTypes.func.isRequired,
  zoom: PropTypes.number,
};
